import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cn } from "~/lib/utils";

const LINE_CLAMP: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
};

/**
 * Fade the tail of the last visible line so "See more" can sit on it, whatever
 * the surface colour behind the text. `1lh` is one line of the paragraph.
 */
const LAST_LINE_FADE = {
  maskImage:
    "linear-gradient(black, black), linear-gradient(to left, transparent 0, transparent 4.25rem, black 7rem)",
  maskSize: "100% calc(100% - 1lh), 100% 1lh",
  maskPosition: "top, bottom",
  maskRepeat: "no-repeat",
  WebkitMaskImage:
    "linear-gradient(black, black), linear-gradient(to left, transparent 0, transparent 4.25rem, black 7rem)",
  WebkitMaskSize: "100% calc(100% - 1lh), 100% 1lh",
  WebkitMaskPosition: "top, bottom",
  WebkitMaskRepeat: "no-repeat",
} as const;

/** Whether a clamped element hides any of its text. */
export const isClamped = (box: { scrollHeight: number; clientHeight: number }) =>
  box.scrollHeight > box.clientHeight + 1;

/**
 * Long prose cut to a few lines, with "See more" only when something is
 * actually hidden. The toggle stays clickable inside cards that route pointer
 * events to an overlay link, and never triggers that link.
 */
export function ClampedText({
  lines = 2,
  className,
  children,
}: {
  readonly lines?: 1 | 2 | 3 | 4;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const id = useId();

  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setOverflows(isClamped(el));
  }, []);

  useEffect(() => {
    if (expanded) return;
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, measure, children]);

  const toggle = (label: string, className: string) => (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={id}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setExpanded((value) => !value);
      }}
      className={cn(
        "pointer-events-auto relative z-10 cursor-pointer text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className,
      )}
    >
      {label}
    </button>
  );
  const fading = !expanded && overflows;
  return (
    <div data-clamped-text={expanded ? "expanded" : "collapsed"} className="relative min-w-0">
      <p
        id={id}
        ref={ref}
        className={cn(!expanded && LINE_CLAMP[lines], className)}
        style={fading ? LAST_LINE_FADE : undefined}
      >
        {children}
        {expanded ? <> {toggle("See less", "ml-1 align-baseline")}</> : null}
      </p>
      {fading ? toggle("See more", "absolute bottom-0 right-0 leading-[inherit]") : null}
    </div>
  );
}
